<template>
  <div>
    <!-- Image Preview Modal -->
    <div
      v-if="showImageModal"
      class="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm"
      @click="closeImageModal"
      @touchstart="handleTouchStart"
      @touchmove="handleTouchMove"
      @touchend="handleTouchEnd"
    >
      <div class="relative max-w-4xl w-full max-h-[90vh] mx-4" @click.stop>
        <!-- Modal header -->
        <div
          class="absolute top-0 left-0 right-0 z-10 flex items-center justify-between p-4"
        >
          <div class="flex items-center gap-3">
            <h3 class="text-xl sm:text-2xl font-bold text-white bg-black/90 backdrop-blur-md px-5 py-3 rounded-2xl shadow-2xl border border-white/30">
              {{ selectedName }}
            </h3>
            <span class="text-sm text-gray-300 bg-black/60 backdrop-blur-md px-3 py-1.5 rounded-full border border-white/20">
              {{ currentModalIndex + 1 }} / {{ availableEntries.length }}
            </span>
          </div>
          <div class="flex items-center gap-2">
            <!-- Re-blur licence plate (for cases where the original detector
                 missed a personalised plate like "SPRUH"). -->
            <button
              v-if="canReblurCurrentEntry && !isManualBlurMode"
              @click.stop="reblurCurrentPlate"
              :disabled="isReblurring"
              class="px-4 py-3 rounded-2xl bg-black/90 text-white text-sm font-semibold hover:bg-blue-600 disabled:bg-black/60 disabled:cursor-not-allowed transition-all duration-200 shadow-2xl backdrop-blur-md border-2 border-white/30 hover:border-blue-400 flex items-center gap-2"
              :title="reblurStatusMessage || 'Re-run plate blur on this photo'"
            >
              <svg v-if="!isReblurring" class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2.5">
                <path stroke-linecap="round" stroke-linejoin="round" d="M4 4v6h6M20 20v-6h-6M5 19a9 9 0 0014.5 2M19 5A9 9 0 004.5 3" />
              </svg>
              <svg v-else class="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="3" opacity="0.25" />
                <path fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
              </svg>
              <span class="hidden sm:inline">{{ isReblurring ? 'Re-blurring…' : 'Re-blur plate' }}</span>
            </button>
            <!-- Manual blur: admin drags box(es) over a missed plate.
                 Staff-only — rewrites the published photo, so gated behind a
                 Better Auth session (the function re-verifies server-side). -->
            <button
              v-if="canReblurCurrentEntry && isLoggedIn && !isManualBlurMode"
              @click.stop="startManualBlur"
              :disabled="isReblurring || isUnblurring || isManualBlurring"
              class="px-4 py-3 rounded-2xl bg-black/90 text-white text-sm font-semibold hover:bg-purple-600 disabled:bg-black/60 disabled:cursor-not-allowed transition-all duration-200 shadow-2xl backdrop-blur-md border-2 border-white/30 hover:border-purple-400 flex items-center gap-2"
              :title="'Draw box(es) over a missed plate, then save'"
            >
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2.5">
                <path stroke-linecap="round" stroke-linejoin="round" d="M9.53 16.122a3 3 0 00-5.78 1.128 2.25 2.25 0 01-2.4 2.245 4.5 4.5 0 008.4-2.245c0-.399-.078-.78-.22-1.128zm0 0a15.998 15.998 0 003.388-1.62m-5.043-.025a15.994 15.994 0 011.622-3.395m3.42 3.42a15.995 15.995 0 004.764-4.648l3.876-5.814a1.151 1.151 0 00-1.597-1.597L14.146 6.32a15.996 15.996 0 00-4.649 4.763m3.42 3.42a6.776 6.776 0 00-3.42-3.42" />
              </svg>
              <span class="hidden sm:inline">Manual blur</span>
            </button>
            <!-- Manual-blur active controls: Save / Cancel -->
            <button
              v-if="isManualBlurMode"
              @click.stop="saveManualBlur"
              :disabled="isManualBlurring || manualBlurRegions.length === 0"
              class="px-4 py-3 rounded-2xl bg-purple-600 text-white text-sm font-semibold hover:bg-purple-500 disabled:bg-black/60 disabled:cursor-not-allowed transition-all duration-200 shadow-2xl backdrop-blur-md border-2 border-purple-300/50 flex items-center gap-2"
              :title="manualBlurRegions.length === 0 ? 'Draw at least one box first' : 'Blur the drawn region(s) and save'"
            >
              <svg v-if="!isManualBlurring" class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2.5">
                <path stroke-linecap="round" stroke-linejoin="round" d="M4.5 12.75l6 6 9-13.5" />
              </svg>
              <svg v-else class="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="3" opacity="0.25" />
                <path fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
              </svg>
              <span class="hidden sm:inline">{{ isManualBlurring ? 'Saving…' : `Save blur${manualBlurRegions.length ? ' (' + manualBlurRegions.length + ')' : ''}` }}</span>
            </button>
            <button
              v-if="isManualBlurMode"
              @click.stop="cancelManualBlur"
              :disabled="isManualBlurring"
              class="px-4 py-3 rounded-2xl bg-black/90 text-white text-sm font-semibold hover:bg-gray-700 disabled:bg-black/60 disabled:cursor-not-allowed transition-all duration-200 shadow-2xl backdrop-blur-md border-2 border-white/30 flex items-center gap-2"
              :title="'Exit manual blur without saving'"
            >
              <span class="hidden sm:inline">Cancel</span>
              <svg class="w-5 h-5 sm:hidden" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2.5">
                <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            <!-- Un-blur: restore the archived pre-blur original (for cases
                 where detection blurred a customer instead of a plate).
                 Staff-only: publishes the unredacted photo, so the button is
                 hidden unless a Better Auth session exists and the function
                 endpoint independently verifies the session server-side. -->
            <button
              v-if="canReblurCurrentEntry && isLoggedIn && !isManualBlurMode"
              @click.stop="unblurCurrentEntry"
              :disabled="isUnblurring"
              class="px-4 py-3 rounded-2xl bg-black/90 text-white text-sm font-semibold hover:bg-emerald-600 disabled:bg-black/60 disabled:cursor-not-allowed transition-all duration-200 shadow-2xl backdrop-blur-md border-2 border-white/30 hover:border-emerald-400 flex items-center gap-2"
              :title="'Restore the original unblurred photo'"
            >
              <svg v-if="!isUnblurring" class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2.5">
                <path stroke-linecap="round" stroke-linejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
                <path stroke-linecap="round" stroke-linejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              <svg v-else class="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="3" opacity="0.25" />
                <path fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
              </svg>
              <span class="hidden sm:inline">{{ isUnblurring ? 'Restoring…' : 'Un-blur' }}</span>
            </button>
            <button
              @click="closeImageModal"
              class="p-3 rounded-full bg-black/90 text-white hover:bg-red-600 transition-all duration-200 shadow-2xl backdrop-blur-md border-2 border-white/30 hover:border-red-400"
            >
              <svg
                class="w-7 h-7"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                stroke-width="3"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
        </div>

        <!-- Re-blur status toast (success/error) shown below the header -->
        <div
          v-if="reblurStatusMessage"
          class="absolute top-20 left-1/2 -translate-x-1/2 z-20 px-4 py-2 rounded-xl text-sm font-medium shadow-2xl backdrop-blur-md border max-w-[90%] text-center"
          :class="reblurStatusType === 'error'
            ? 'bg-red-900/90 text-white border-red-400/40'
            : 'bg-emerald-900/90 text-white border-emerald-400/40'"
        >
          {{ reblurStatusMessage }}
        </div>

        <!-- Navigation Buttons -->
        <button
          v-if="currentModalIndex > 0"
          @click.stop="goToPreviousCustomer"
          class="absolute left-4 top-1/2 -translate-y-1/2 z-20 p-4 rounded-full bg-black/90 text-white hover:bg-blue-600 transition-all duration-200 shadow-2xl backdrop-blur-md border-2 border-white/30 hover:border-blue-400"
          aria-label="Previous customer"
        >
          <svg
            class="w-6 h-6"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            stroke-width="3"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              d="M15 19l-7-7 7-7"
            />
          </svg>
        </button>

        <button
          v-if="currentModalIndex < availableEntries.length - 1"
          @click.stop="goToNextCustomer"
          class="absolute right-4 top-1/2 -translate-y-1/2 z-20 p-4 rounded-full bg-black/90 text-white hover:bg-blue-600 transition-all duration-200 shadow-2xl backdrop-blur-md border-2 border-white/30 hover:border-blue-400"
          aria-label="Next customer"
        >
          <svg
            class="w-6 h-6"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            stroke-width="3"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              d="M9 5l7 7-7 7"
            />
          </svg>
        </button>

        <!-- Manual-blur hint banner -->
        <div
          v-if="isManualBlurMode"
          class="absolute top-20 left-1/2 -translate-x-1/2 z-20 px-4 py-2 rounded-xl text-sm font-medium shadow-2xl backdrop-blur-md border bg-purple-900/90 text-white border-purple-400/40 max-w-[90%] text-center pointer-events-none"
        >
          Drag a box over the missed plate. Draw more if needed, then Save.
          <button
            v-if="manualBlurRegions.length"
            @click.stop="clearManualRegions"
            class="ml-2 underline pointer-events-auto hover:text-purple-200"
          >Clear ({{ manualBlurRegions.length }})</button>
        </div>

        <!-- Modal image container with swipe support -->
        <div
          class="w-full relative modal-image-container"
          :style="{ transform: `translateX(${swipeOffset}px)` }"
        >
          <img
            ref="modalImage"
            :src="selectedImage"
            class="w-full h-auto object-contain max-h-[85vh] rounded-3xl border border-white/20 shadow-2xl select-none"
            :class="{ 'swiping': isSwiping }"
            alt="Delivery Photo"
            draggable="false"
          />

          <!-- Drawing overlay (admin manual blur). Sits exactly over the
               rendered image; captures pointer drags to create blur regions
               expressed in normalized 0–1 image coordinates. -->
          <div
            v-if="isManualBlurMode"
            ref="blurOverlay"
            class="absolute inset-0 z-30 cursor-crosshair touch-none"
            @mousedown.stop.prevent="onBlurPointerDown"
            @mousemove.stop.prevent="onBlurPointerMove"
            @mouseup.stop.prevent="onBlurPointerUp"
            @mouseleave="onBlurPointerUp"
            @touchstart.stop.prevent="onBlurPointerDown"
            @touchmove.stop.prevent="onBlurPointerMove"
            @touchend.stop.prevent="onBlurPointerUp"
          >
            <!-- Committed regions -->
            <div
              v-for="(r, i) in manualBlurRegions"
              :key="'region-' + i"
              class="absolute border-2 border-purple-400 bg-purple-500/30 backdrop-blur-sm rounded-sm"
              :style="regionStyle(r)"
            >
              <button
                @mousedown.stop.prevent="removeManualRegion(i)"
                @touchstart.stop.prevent="removeManualRegion(i)"
                class="absolute -top-3 -right-3 w-6 h-6 rounded-full bg-red-600 text-white text-xs font-bold flex items-center justify-center shadow-lg border border-white/40"
                title="Remove this box"
              >×</button>
            </div>
            <!-- In-progress drag rectangle -->
            <div
              v-if="manualDraft"
              class="absolute border-2 border-dashed border-purple-300 bg-purple-400/20 rounded-sm pointer-events-none"
              :style="regionStyle(manualDraft)"
            ></div>
          </div>
        </div>
      </div>
    </div>
    <!-- Your component content here -->
    <!-- <h2>Total Entries List for {{ dateRange }}</h2>
 -->

    <!-- Card Blog -->
    <div
      class="max-w-[48rem] lg:max-w-7xl px-4 py-4 sm:px-6 lg:px-8 lg:py-14 lg:pt-4 mx-auto"
    >
      <!-- Enhanced Switch with Download Button and Search -->
      <div class="mb-6 dark">
        <div
          class="flex flex-col sm:flex-row sm:items-center gap-4 p-4 sm:p-6 rounded-3xl bg-black/60 backdrop-blur-lg border border-white/20 shadow-2xl transform transition-all duration-300 hover:scale-[1.02] card-hover"
        >
          <!-- View Toggle Section -->
          <div class="flex items-center justify-center sm:justify-start bg-black/40 backdrop-blur-sm rounded-full px-3 py-2 border border-white/10 flex-shrink-0">
            <label
              class="min-w-[2.5rem] text-xs sm:text-sm mr-2 sm:mr-3 font-medium transition-colors cursor-pointer"
              :class="
                viewMode === 'gallery' ? 'text-blue-400' : 'text-gray-300'
              "
            >
              <div class="flex items-center justify-center space-x-1 sm:space-x-2">
                <svg
                  class="w-4 h-4 sm:w-5 sm:h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z"
                  />
                </svg>
                <span class="hidden sm:inline">Gallery</span>
              </div>
            </label>

            <label class="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                v-model="isListView"
                @change="toggleViewMode"
                class="sr-only peer"
              />
              <div
                class="w-11 h-6 sm:w-12 sm:h-7 bg-gray-700 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 sm:after:h-6 sm:after:w-6 after:transition-all dark:border-gray-600 peer-checked:bg-gradient-to-r peer-checked:from-blue-500 peer-checked:to-blue-600"
              ></div>
            </label>

            <label
              class="min-w-[2.5rem] text-xs sm:text-sm ml-2 sm:ml-3 font-medium transition-colors cursor-pointer"
              :class="viewMode === 'list' ? 'text-blue-400' : 'text-gray-300'"
            >
              <div class="flex items-center justify-center space-x-1 sm:space-x-2">
                <svg
                  class="w-4 h-4 sm:w-5 sm:h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M4 6h16M4 10h16M4 14h16M4 18h16"
                  />
                </svg>
                <span class="hidden sm:inline">List</span>
              </div>
            </label>
          </div>

          <!-- Enhanced Search -->
          <div class="relative flex-1 min-w-0">
            <div
              class="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none z-10"
            >
              <svg
                class="h-5 w-5 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
            </div>
            <input
              v-model="searchQuery"
              type="text"
              placeholder="Search by name, email, phone, or salesperson..."
              class="search-input pl-12 pr-4 py-4 border border-white/20 rounded-full w-full backdrop-blur-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-green-400 focus:border-green-400/50 transition-all duration-200 shadow-lg"
            />
          </div>

          <!-- Action Buttons Container -->
          <div
            class="flex flex-col sm:flex-row gap-2 sm:gap-3 flex-shrink-0 w-full sm:w-auto"
          >
            <!-- Download Button -->
            <button
              @click="$emit('download-csv')"
              class="inline-flex items-center justify-center px-6 py-3 sm:px-8 sm:py-3 text-sm sm:text-base font-medium text-blue-600 bg-white hover:bg-gray-100 rounded-full shadow-md focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-offset-2 focus:ring-offset-gray-900 transition-all duration-300 h-[50px] sm:h-[50px]"
              :disabled="isDownloading"
            >
              <svg
                v-if="!isDownloading"
                class="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M19 14l-7 7m0 0l-7-7m7 7V3"
                />
              </svg>
              <svg
                v-else
                class="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2 animate-spin"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  class="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  stroke-width="4"
                ></circle>
                <path
                  class="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
              <span class="whitespace-nowrap">{{
                isDownloading ? "Generating..." : "Download CSV"
              }}</span>
            </button>
          </div>
        </div>
      </div>
      <!-- End Switch -->

      <!-- Enhanced Grid -->
      <div v-if="viewMode === 'gallery'" class="grid md:grid-cols-2 gap-5">
        <!-- Enhanced Gallery Card -->
        <div
          v-for="entry in paginatedEntries"
          :key="entry.id"
          class="group relative flex flex-col h-full transform transition-all duration-300 hover:scale-[1.02] card-hover bg-black/40 backdrop-blur-md border border-white/10 rounded-3xl overflow-hidden shadow-xl"
        >
          <!-- Full Car Image at Top -->
          <div
            class="relative w-full rounded-t-3xl overflow-hidden h-[500px] lg:h-[550px] shadow-inner bg-black/40"
          >
            <img
              class="w-full h-full absolute top-0 left-0 object-cover object-center cursor-pointer"
              :src="entry['13']"
              alt="Delivery Photo"
              @click.stop="openImageModal(entry['13'], entry['1'])"
            />
            <!-- Gradient overlay for better text readability -->
            <div
              class="absolute inset-0 bg-gradient-to-b from-black/60 via-transparent to-black/70 pointer-events-none"
            ></div>
          </div>

          <!-- Contact Information (same as list view) -->
          <div class="p-4">
            <!-- Name and Date - Mobile: stacked, Desktop: side by side -->
            <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-3">
              <h3 class="text-lg sm:text-xl font-bold text-white truncate text-center sm:text-left">
                {{ entry["1"] }}
              </h3>
              <!-- Date Badge -->
              <div class="flex items-center justify-center sm:justify-start bg-black/40 backdrop-blur-sm px-3 py-1 rounded-full border border-white/10 flex-shrink-0 mt-2 sm:mt-0 sm:ml-2">
                <svg class="w-4 h-4 text-blue-400 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                <span class="text-sm text-white/90 font-medium">{{ formatDate(entry["date_created"]) }}</span>
              </div>
            </div>

            <!-- Contact Details Row (Mobile Optimized) -->
            <div class="flex flex-col sm:flex-row gap-2 sm:space-x-2 mt-3 w-full">
              <!-- Phone Number Button -->
              <a
                :href="
                  formatPhone(findPhoneField(entry)) !== 'No Phone'
                    ? `tel:${formatPhone(findPhoneField(entry))}`
                    : '#'
                "
                class="contact-detail-button bg-green-500 flex-1 py-4"
                :class="
                  formatPhone(findPhoneField(entry)) === 'No Phone'
                    ? 'opacity-50 cursor-not-allowed'
                    : 'hover:bg-green-600'
                "
                @click="
                  formatPhone(findPhoneField(entry)) === 'No Phone'
                    ? $event.preventDefault()
                    : null
                "
              >
                <svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4 sm:w-5 sm:h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                </svg>
                <span
                  class="text-sm sm:text-base font-semibold text-white truncate max-w-[120px] sm:max-w-none"
                  :class="
                    formatPhone(findPhoneField(entry)) === 'No Phone'
                      ? 'opacity-50'
                      : ''
                  "
                >
                  {{ formatPhone(findPhoneField(entry)) }}
                </span>
              </a>

              <!-- Email Button -->
              <a
                :href="`mailto:${entry['2']}`"
                class="contact-detail-button bg-blue-500 flex-1 hover:bg-blue-600 py-4"
              >
                <svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4 sm:w-5 sm:h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                <span class="text-sm sm:text-base font-semibold text-white truncate max-w-[120px] sm:max-w-none">
                  {{ entry["2"] }}
                </span>
              </a>
            </div>

            <!-- Salesperson and Car Purchased Info -->
            <div class="mt-4 flex items-center justify-center sm:justify-start gap-2 flex-wrap">
              <!-- Salesperson Badge -->
              <div class="flex items-center backdrop-blur-sm px-3 py-1.5 rounded-full border transition-all duration-200" :class="getSalespersonBadgeClasses(entry['9']).container">
                <svg
                  class="w-4 h-4 mr-1.5"
                  :class="getSalespersonBadgeClasses(entry['9']).icon"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                  />
                </svg>
                <span class="text-sm font-semibold" :class="getSalespersonBadgeClasses(entry['9']).text">{{ entry["9"] || "Sales Team" }}</span>
              </div>
              <!-- Car Purchased Badge -->
              <div class="flex items-center bg-gradient-to-r from-blue-500/20 to-cyan-500/20 backdrop-blur-sm px-3 py-1.5 rounded-full border border-blue-400/40">
                <svg
                  class="w-4 h-4 text-cyan-300 mr-1.5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z"
                  />
                </svg>
                <span class="text-sm text-cyan-200 font-semibold">{{ entry["4"] || "KIA Vehicle" }}</span>
              </div>
            </div>
          </div>
        </div>
        <!-- End Card -->
      </div>
      <!-- End Grid -->

      <!-- iOS-Style Contact List View -->
      <div v-else-if="viewMode === 'list'" class="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <div
          v-for="entry in paginatedEntries"
          :key="entry.id"
          class="bg-black/40 backdrop-blur-xl border border-white/10 shadow-lg rounded-3xl overflow-hidden transform transition-all duration-300 hover:bg-black/50 active:scale-[0.99]"
        >
          <!-- iOS Contact Card Style -->
          <div class="p-4">
            <!-- Mobile: Stack picture, name, date above buttons -->
            <div class="flex flex-col sm:flex-row sm:items-start sm:space-x-4">
              <!-- Profile Image - iOS style circular with border (larger size) -->
              <div class="flex-shrink-0 relative mb-3 sm:mb-0">
                <img
                  class="h-16 sm:h-20 md:h-24 w-16 sm:w-20 md:w-24 rounded-full object-cover cursor-pointer ring-2 ring-white/20 shadow-lg mx-auto sm:mx-0"
                  :src="entry['13']"
                  alt="Contact Photo"
                  @click.stop="openImageModal(entry['13'], entry['1'])"
                />
              </div>

              <!-- Contact Information -->
              <div class="flex-1 min-w-0">
                <!-- Name and Date - Mobile: stacked, Desktop: side by side -->
                <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-3">
                  <h3 class="text-lg sm:text-xl font-bold text-white truncate text-center sm:text-left">
                    {{ entry["1"] }}
                  </h3>
                  <!-- Date Badge -->
                  <div class="flex items-center justify-center sm:justify-start bg-black/40 backdrop-blur-sm px-3 py-1 rounded-full border border-white/10 flex-shrink-0 mt-2 sm:mt-0 sm:ml-2">
                    <svg class="w-4 h-4 text-blue-400 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    <span class="text-sm text-white/90 font-medium">{{ formatDate(entry["date_created"]) }}</span>
                  </div>
                </div>

                <!-- Contact Details Row (Mobile Optimized) -->
                <div class="flex flex-col sm:flex-row gap-2 sm:space-x-2 mt-3 w-full">
                  <!-- Phone Number Button -->
                  <a
                    :href="
                      formatPhone(findPhoneField(entry)) !== 'No Phone'
                        ? `tel:${formatPhone(findPhoneField(entry))}`
                        : '#'
                    "
                    class="contact-detail-button bg-green-500 flex-1 py-4"
                    :class="
                      formatPhone(findPhoneField(entry)) === 'No Phone'
                        ? 'opacity-50 cursor-not-allowed'
                        : 'hover:bg-green-600'
                    "
                    @click="
                      formatPhone(findPhoneField(entry)) === 'No Phone'
                        ? $event.preventDefault()
                        : null
                    "
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4 sm:w-5 sm:h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                    </svg>
                    <span
                      class="text-sm sm:text-base font-semibold text-white truncate max-w-[120px] sm:max-w-none"
                      :class="
                        formatPhone(findPhoneField(entry)) === 'No Phone'
                          ? 'opacity-50'
                          : ''
                      "
                    >
                      {{ formatPhone(findPhoneField(entry)) }}
                    </span>
                  </a>

                  <!-- Email Button -->
                  <a
                    :href="`mailto:${entry['2']}`"
                    class="contact-detail-button bg-blue-500 flex-1 hover:bg-blue-600 py-4"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4 sm:w-5 sm:h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                    <span class="text-sm sm:text-base font-semibold text-white truncate max-w-[120px] sm:max-w-none">
                      {{ entry["2"] }}
                    </span>
                  </a>
                </div>

                <!-- Salesperson and Car Purchased Info -->
                <div class="mt-4 flex items-center justify-center sm:justify-start gap-2 flex-wrap">
                  <!-- Salesperson Badge -->
                  <div class="flex items-center backdrop-blur-sm px-3 py-1.5 rounded-full border transition-all duration-200" :class="getSalespersonBadgeClasses(entry['9']).container">
                    <svg
                      class="w-4 h-4 mr-1.5"
                      :class="getSalespersonBadgeClasses(entry['9']).icon"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                      />
                    </svg>
                    <span class="text-sm font-semibold" :class="getSalespersonBadgeClasses(entry['9']).text">{{ entry["9"] || "Sales Team" }}</span>
                  </div>
                  <!-- Car Purchased Badge -->
                  <div class="flex items-center bg-gradient-to-r from-blue-500/20 to-cyan-500/20 backdrop-blur-sm px-3 py-1.5 rounded-full border border-blue-400/40">
                    <svg
                      class="w-4 h-4 text-cyan-300 mr-1.5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z"
                      />
                    </svg>
                    <span class="text-sm text-cyan-200 font-semibold">{{ entry["4"] || "KIA Vehicle" }}</span>
                  </div>
                </div>

                <!-- Device Info Expandable (iOS style) -->
                <div v-if="entry['user_agent']" class="mt-3">
                  <details class="group">
                    <summary
                      class="flex items-center space-x-2 cursor-pointer select-none"
                    >
                      <svg
                        class="w-3.5 h-3.5 text-gray-400 transform transition-transform group-open:rotate-90"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2"
                          d="M9 5l7 7-7 7"
                        />
                      </svg>
                      <span class="text-xs text-gray-400"
                        >Browser & Device Info</span
                      >
                    </summary>
                    <div class="mt-2 pl-5">
                      <p
                        class="text-xs text-gray-500 leading-relaxed break-words"
                      >
                        {{ entry["user_agent"] }}
                      </p>
                    </div>
                  </details>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Check if there are no filtered entries -->
      <div
        v-if="filteredEntries.length === 0"
        class="text-center my-4 text-gray-300"
      >
        No results found for "{{ searchQuery }}"
      </div>

      <!-- Enhanced Pagination Controls -->
      <div
        v-if="filteredEntries.length > 0"
        class="flex flex-col items-center mt-8 mb-20 md:mb-12 space-y-3"
      >
        <!-- Page info -->
        <div class="text-sm text-gray-300 text-center">
          <div>Page {{ currentPage }} of {{ totalPages }}</div>
          <div class="text-xs text-gray-400 mt-1">
            Showing {{ (currentPage - 1) * dynamicEntriesPerPage + 1 }}-{{
              Math.min(
                currentPage * dynamicEntriesPerPage,
                filteredEntries.length
              )
            }}
            of {{ filteredEntries.length }} entries
            <span
              v-if="dynamicEntriesPerPage !== entriesPerPage"
              class="ml-2 text-blue-400"
            >
              ({{ dynamicEntriesPerPage }} per page)
            </span>
          </div>
        </div>

        <!-- Pagination buttons -->
        <div
          class="inline-flex rounded-3xl overflow-hidden shadow-lg bg-black/70 backdrop-blur-xl border border-white/30"
        >
          <button
            @click="prevPage"
            :disabled="currentPage <= 1"
            class="px-6 py-4 text-sm sm:text-base font-medium transition-all duration-200"
            :class="
              currentPage <= 1
                ? 'text-gray-500 cursor-not-allowed'
                : 'text-white hover:bg-white/10 hover:text-blue-400'
            "
          >
            Prev
          </button>

          <!-- Page number indicator -->
          <div
            class="flex items-center px-4 bg-blue-600/30 border-x border-white/20"
          >
            <span class="text-sm font-medium text-blue-400">{{
              currentPage
            }}</span>
          </div>

          <button
            @click="nextPage"
            :disabled="currentPage >= totalPages"
            class="px-6 py-4 text-sm sm:text-base font-medium transition-all duration-200"
            :class="
              currentPage >= totalPages
                ? 'text-gray-500 cursor-not-allowed'
                : 'text-white hover:bg-white/10 hover:text-blue-400'
            "
          >
            Next
          </button>
        </div>
      </div>
    </div>
    <!-- End Card Blog -->
  </div>
</template>

<script>
import axios from "axios";
import { auth } from "@/utils/betterAuth";

export default {
  name: "welcome-entries",
  props: {
    dateRange: Object, // Prop to accept the date range from the parent component
    entries: Array, // Prop to accept the entries data from the parent component
    selectedSalesperson: String, // Currently selected salesperson for filtering
    isDownloading: Boolean, // Loading state for CSV download
  },
  data() {
    return {
      currentPage: 1,
      entriesPerPage: 12, // Adjust this to display the desired number of entries per page
      searchQuery: "",
      isListView: true,
      viewMode: "list", // default view changed to list
      showImageModal: false,
      selectedImage: null,
      selectedName: "",
      currentModalIndex: 0, // Current customer index in modal
      touchStartX: 0, // Touch start position for swipe detection
      touchStartY: 0,
      swipeOffset: 0, // Current swipe offset for animation
      isSwiping: false, // Flag to track if user is actively swiping
      isReblurring: false,
      isUnblurring: false,
      isLoggedIn: false, // Better Auth session present — gates Un-blur + manual blur
      reblurStatusMessage: "",
      reblurStatusType: "", // "" | "success" | "error"
      // Manual blur (admin drag-to-blur) state
      isManualBlurMode: false, // overlay active
      isManualBlurring: false, // save request in flight
      manualBlurRegions: [], // committed regions, normalized {left,top,width,height}
      manualDraft: null, // in-progress drag rectangle (normalized) or null
      manualDragStart: null, // {x,y} normalized anchor of current drag
    };
  },
  computed: {
    selectedDateRange() {
      return this.dateRange || null;
    },
    // Get all entries that have images for modal navigation
    availableEntries() {
      const entriesArray = Array.isArray(this.filteredEntries) ? this.filteredEntries : [];
      return entriesArray.filter(entry => entry['13'] && entry['13'].trim() !== '');
    },
    currentModalEntry() {
      return this.availableEntries[this.currentModalIndex] || null;
    },
    canReblurCurrentEntry() {
      const e = this.currentModalEntry;
      if (!e || !e.id || !e.form_id || !e['13']) return false;
      // Successful save replaces field 13 with the new R2 URL — keep the
      // button available in case the user wants to run a second pass
      // against the saved version (idempotent on already-blurred regions).
      return true;
    },
    filteredEntries() {
      // Ensure entries is an array
      const entriesArray = Array.isArray(this.entries) ? this.entries : [];

      if (entriesArray.length === 0) {
        return [];
      }

      let filtered = entriesArray;

      // Apply salesperson filter first if one is selected
      if (this.selectedSalesperson) {
        filtered = filtered.filter(
          (entry) => entry["9"] === this.selectedSalesperson
        );
      }

      // Then apply search query filter
      if (this.searchQuery) {
        const query = this.searchQuery.toLowerCase();
        filtered = filtered.filter((entry) => {
          // Search in name (field 1)
          const nameMatch =
            entry["1"] && String(entry["1"]).toLowerCase().includes(query);

          // Search in email (field 2)
          const emailMatch =
            entry["2"] && String(entry["2"]).toLowerCase().includes(query);

          // Search in phone number (field 8, with proper formatting)
          const phoneField = this.findPhoneField(entry);
          const formattedPhone = this.formatPhone(phoneField);

          // Create searchable phone versions (removing common formatting)
          const phoneForSearch =
            formattedPhone && formattedPhone !== "No Phone"
              ? String(formattedPhone).replace(/[\s\-()+.]/g, "")
              : "";
          const queryForPhone = query.replace(/[\s\-()+.]/g, "");

          const phoneMatch =
            phoneForSearch && phoneForSearch.includes(queryForPhone);

          // Search in raw phone field too (in case formatting removes characters)
          const rawPhoneForSearch = phoneField
            ? String(phoneField).replace(/[\s\-()+.[\]'"]/g, "")
            : "";
          const rawPhoneMatch =
            rawPhoneForSearch && rawPhoneForSearch.includes(queryForPhone);

          // Search in salesperson (field 9)
          const salespersonMatch =
            entry["9"] && String(entry["9"]).toLowerCase().includes(query);

          return (
            nameMatch ||
            emailMatch ||
            phoneMatch ||
            rawPhoneMatch ||
            salespersonMatch
          );
        });
      }

      return filtered;
    },
    paginatedEntries() {
      if (!this.filteredEntries || this.filteredEntries.length === 0) {
        return [];
      }

      const start = (this.currentPage - 1) * this.dynamicEntriesPerPage;
      const end = start + this.dynamicEntriesPerPage;

      return this.filteredEntries.slice(start, end);
    },
    totalPages() {
      if (!this.filteredEntries || this.filteredEntries.length === 0) {
        return 1;
      }

      const pages = Math.max(
        1,
        Math.ceil(this.filteredEntries.length / this.dynamicEntriesPerPage)
      );
      return pages;
    },

    // Dynamically adjust entries per page based on total entries count
    dynamicEntriesPerPage() {
      if (this.filteredEntries && this.filteredEntries.length > 500) {
        return 24; // Show more entries per page for large datasets
      } else if (this.filteredEntries && this.filteredEntries.length > 100) {
        return 18; // Medium datasets
      }
      return this.entriesPerPage; // Default for smaller datasets
    },

    // Calculate salesperson rankings
    salespersonRankings() {
      if (!this.entries || this.entries.length === 0) {
        return {};
      }

      // Count entries per salesperson
      const salesCount = this.entries.reduce((acc, entry) => {
        const salesperson = entry['9'];
        if (salesperson) {
          acc[salesperson] = (acc[salesperson] || 0) + 1;
        }
        return acc;
      }, {});

      // Sort and assign ranks
      const sortedSalespersons = Object.entries(salesCount)
        .sort((a, b) => b[1] - a[1]);

      // Create rank map
      const rankings = {};
      sortedSalespersons.forEach(([name], index) => {
        rankings[name] = index;
      });

      return rankings;
    },
  },
  mounted() {
  },
  watch: {
    searchQuery() {
      this.currentPage = 1;
    },
    selectedSalesperson() {
      this.currentPage = 1;
    },
    entries: {
      handler() {
        this.currentPage = 1;
      },
      deep: true,
    },
  },
  methods: {
    getSalespersonBadgeClasses(salesperson) {
      if (!salesperson) {
        return {
          container: 'bg-gradient-to-r from-slate-600/20 to-slate-700/20 border-slate-500/40',
          icon: 'text-slate-300',
          text: 'text-slate-200'
        };
      }

      const rank = this.salespersonRankings[salesperson];
      
      switch (rank) {
        case 0: // 1st place - Gold
          return {
            container: 'bg-gradient-to-r from-yellow-400/20 to-yellow-500/20 border-yellow-400/40',
            icon: 'text-yellow-300',
            text: 'text-yellow-200'
          };
        case 1: // 2nd place - Silver
          return {
            container: 'bg-gradient-to-r from-gray-200/20 to-gray-400/20 border-gray-300/40',
            icon: 'text-gray-200',
            text: 'text-gray-100'
          };
        case 2: // 3rd place - Bronze
          return {
            container: 'bg-gradient-to-r from-orange-400/20 to-orange-500/20 border-orange-400/40',
            icon: 'text-orange-300',
            text: 'text-orange-200'
          };
        default: // Other places
          return {
            container: 'bg-gradient-to-r from-slate-600/20 to-slate-700/20 border-slate-500/40',
            icon: 'text-slate-300',
            text: 'text-slate-200'
          };
      }
    },

    formatDate(inputDate) {
      // Parse the input date string
      const date = new Date(inputDate);

      // Check if the date is valid
      if (isNaN(date.getTime())) {
        return "Today";
      }

      // Extract day, month, and year
      const day = date.getDate().toString().padStart(2, "0");
      const monthNames = [
        "Jan",
        "Feb",
        "Mar",
        "Apr",
        "May",
        "Jun",
        "Jul",
        "Aug",
        "Sep",
        "Oct",
        "Nov",
        "Dec",
      ];
      const month = monthNames[date.getMonth()];
      const year = date.getFullYear();

      // Return the formatted date
      return `${month} ${day}, ${year}`;
    },
    formatDateRange(dateRange) {
      if (!dateRange || !dateRange.startDate || !dateRange.endDate) {
        return "All dates";
      }
      const formatDate = (dateString) => {
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return dateString;
        const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
        return `${monthNames[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`;
      };
      return `${formatDate(dateRange.startDate)} - ${formatDate(dateRange.endDate)}`;
    },
    clearDateRangeFilter() {
      // Emit event to parent to clear date range filter
      this.$emit('clear-date-range-filter');
    },
    toggleViewMode() {
      this.viewMode = this.isListView ? "list" : "gallery";
    },
    openImageModal(imageUrl, name) {
      // Find the index of the clicked entry
      const entryIndex = this.availableEntries.findIndex(
        entry => entry['13'] === imageUrl && entry['1'] === name
      );
      
      this.currentModalIndex = entryIndex >= 0 ? entryIndex : 0;
      this.updateModalContent();
      this.showImageModal = true;

      // Refresh login state each time the modal opens so the Un-blur button
      // appears/disappears with the Better Auth session.
      auth.isAuthenticated().then((ok) => { this.isLoggedIn = !!ok; }).catch(() => { this.isLoggedIn = false; });

      // Add a class to prevent scrolling when modal is open
      document.body.classList.add("overflow-hidden");

      // Add event listener for keyboard navigation
      window.addEventListener("keydown", this.handleModalKeyEvents);
    },
    updateModalContent() {
      if (this.availableEntries.length === 0) return;

      const entry = this.availableEntries[this.currentModalIndex];
      if (entry) {
        // entry['13'] is mutated in place by reblurCurrentPlate on save,
        // so navigating away and back always shows the latest image.
        this.selectedImage = entry['13'];
        this.selectedName = entry['1'] || "Photo Preview";
        // Clear any per-entry toast when the viewer changes entry.
        this.reblurStatusMessage = "";
        this.reblurStatusType = "";
        // Changing entry must drop any in-progress manual-blur selection so a
        // box drawn for one photo can't be saved against another.
        this.isManualBlurMode = false;
        this.manualBlurRegions = [];
        this.manualDraft = null;
        this.manualDragStart = null;
      }
    },
    async reblurCurrentPlate() {
      const entry = this.currentModalEntry;
      if (!entry || !entry.id || !entry.form_id || !entry['13']) return;
      if (this.isReblurring) return;

      this.isReblurring = true;
      this.reblurStatusMessage = "";
      this.reblurStatusType = "";

      try {
        const { data } = await axios.post(
          '/.netlify/functions/reblur-entry',
          {
            entryId: entry.id,
            formId: entry.form_id,
            imageUrl: entry['13'],
          },
          { timeout: 60000 }
        );

        if (data && data.saved && data.newImageUrl) {
          // Saved permanently. Cache-bust so the <img> definitely reloads
          // even if Cloudflare's CDN had cached a previous re-blur for the
          // same key (we use random suffixes so collisions are unlikely
          // but possible across same-second submissions).
          const bust = `${data.newImageUrl}${data.newImageUrl.includes('?') ? '&' : '?'}t=${Date.now()}`;
          // Mutate field 13 in place so list/gallery/exports reflect the
          // new URL without a page reload.
          entry['13'] = bust;
          this.selectedImage = bust;
          this.reblurStatusType = "success";
          this.reblurStatusMessage = `Re-blurred and saved (${data.platesDetected} plate${data.platesDetected === 1 ? '' : 's'} detected)`;
        } else if (data && data.success && data.saved === false) {
          // Detection found nothing — function intentionally skipped the
          // save to avoid polluting the bucket with redundant copies.
          this.reblurStatusType = "success";
          this.reblurStatusMessage = data.reason || 'No plates detected — nothing to save';
        } else {
          this.reblurStatusType = "error";
          this.reblurStatusMessage = (data && data.error) || 'Re-blur failed';
        }
      } catch (err) {
        const detail = err.response?.data?.error || err.message || 'Network error';
        this.reblurStatusType = "error";
        this.reblurStatusMessage = `Re-blur failed: ${detail}`;
      } finally {
        this.isReblurring = false;
        const lifeMs = this.reblurStatusType === 'success' ? 5000 : 8000;
        const myMsg = this.reblurStatusMessage;
        setTimeout(() => {
          if (this.reblurStatusMessage === myMsg) {
            this.reblurStatusMessage = "";
            this.reblurStatusType = "";
          }
        }, lifeMs);
      }
    },
    async unblurCurrentEntry() {
      const entry = this.currentModalEntry;
      if (!entry || !entry.id || !entry.form_id) return;
      if (this.isUnblurring || this.isReblurring) return;
      if (!this.isLoggedIn || !auth.sessionToken) {
        this.reblurStatusType = "error";
        this.reblurStatusMessage = "Sign in required to un-blur photos";
        return;
      }
      if (!window.confirm('Restore the original unblurred photo for this entry? Plates in the photo will become visible again (you can Re-blur afterwards).')) return;

      this.isUnblurring = true;
      this.reblurStatusMessage = "";
      this.reblurStatusType = "";

      try {
        const { data } = await axios.post(
          '/.netlify/functions/unblur-entry',
          {
            entryId: entry.id,
            formId: entry.form_id,
            // Dealership baseplate gets re-composited server-side because the
            // archive stores the raw photo without it.
            baseplateUrl: this.$store?.state?.accountData?.[0]?.baseplate || null,
          },
          {
            timeout: 60000,
            headers: { Authorization: `Bearer ${auth.sessionToken}` },
          }
        );

        if (data && data.saved && data.newImageUrl) {
          const bust = `${data.newImageUrl}${data.newImageUrl.includes('?') ? '&' : '?'}t=${Date.now()}`;
          // Mutate field 13 in place so list/gallery/exports reflect the
          // new URL without a page reload (same pattern as re-blur).
          entry['13'] = bust;
          this.selectedImage = bust;
          this.reblurStatusType = "success";
          this.reblurStatusMessage = data.baseplateApplied
            ? 'Original photo restored'
            : 'Original photo restored (without baseplate)';
        } else {
          this.reblurStatusType = "error";
          this.reblurStatusMessage = (data && data.error) || 'Un-blur failed';
        }
      } catch (err) {
        const detail = err.response?.data?.error || err.message || 'Network error';
        this.reblurStatusType = "error";
        this.reblurStatusMessage = `Un-blur failed: ${detail}`;
      } finally {
        this.isUnblurring = false;
        const lifeMs = this.reblurStatusType === 'success' ? 5000 : 8000;
        const myMsg = this.reblurStatusMessage;
        setTimeout(() => {
          if (this.reblurStatusMessage === myMsg) {
            this.reblurStatusMessage = "";
            this.reblurStatusType = "";
          }
        }, lifeMs);
      }
    },
    // ---- Manual blur (admin drag-to-blur) ----
    startManualBlur() {
      if (!this.isLoggedIn) {
        this.reblurStatusType = "error";
        this.reblurStatusMessage = "Sign in required to manually blur photos";
        return;
      }
      this.isManualBlurMode = true;
      this.manualBlurRegions = [];
      this.manualDraft = null;
      this.manualDragStart = null;
    },
    cancelManualBlur() {
      this.isManualBlurMode = false;
      this.manualBlurRegions = [];
      this.manualDraft = null;
      this.manualDragStart = null;
    },
    clearManualRegions() {
      this.manualBlurRegions = [];
    },
    removeManualRegion(i) {
      this.manualBlurRegions.splice(i, 1);
    },
    // Inline style for a normalized region rendered over the image.
    regionStyle(r) {
      return {
        left: `${r.left * 100}%`,
        top: `${r.top * 100}%`,
        width: `${r.width * 100}%`,
        height: `${r.height * 100}%`,
      };
    },
    // Pointer position as normalized 0–1 coords relative to the rendered image.
    // The overlay covers the container, but object-contain may letterbox the
    // image; we clamp to the image's actual drawn rect so boxes never extend
    // into the letterbox bars.
    normalizedPointFromEvent(e) {
      const img = this.$refs.modalImage;
      if (!img) return null;
      const rect = img.getBoundingClientRect();
      const point = e.touches && e.touches.length
        ? e.touches[0]
        : (e.changedTouches && e.changedTouches.length ? e.changedTouches[0] : e);
      const clientX = point.clientX;
      const clientY = point.clientY;
      // Compute the actual drawn image rect inside the element (object-contain).
      const natW = img.naturalWidth || rect.width;
      const natH = img.naturalHeight || rect.height;
      const scale = Math.min(rect.width / natW, rect.height / natH);
      const drawnW = natW * scale;
      const drawnH = natH * scale;
      const offsetX = (rect.width - drawnW) / 2;
      const offsetY = (rect.height - drawnH) / 2;
      const x = (clientX - rect.left - offsetX) / drawnW;
      const y = (clientY - rect.top - offsetY) / drawnH;
      return {
        x: Math.max(0, Math.min(1, x)),
        y: Math.max(0, Math.min(1, y)),
      };
    },
    onBlurPointerDown(e) {
      if (!this.isManualBlurMode || this.isManualBlurring) return;
      const p = this.normalizedPointFromEvent(e);
      if (!p) return;
      this.manualDragStart = p;
      this.manualDraft = { left: p.x, top: p.y, width: 0, height: 0 };
    },
    onBlurPointerMove(e) {
      if (!this.isManualBlurMode || !this.manualDragStart) return;
      const p = this.normalizedPointFromEvent(e);
      if (!p) return;
      const s = this.manualDragStart;
      this.manualDraft = {
        left: Math.min(s.x, p.x),
        top: Math.min(s.y, p.y),
        width: Math.abs(p.x - s.x),
        height: Math.abs(p.y - s.y),
      };
    },
    onBlurPointerUp() {
      if (!this.isManualBlurMode || !this.manualDragStart) return;
      const d = this.manualDraft;
      this.manualDragStart = null;
      this.manualDraft = null;
      // Ignore accidental taps / hairline drags.
      if (d && d.width > 0.01 && d.height > 0.01) {
        this.manualBlurRegions.push(d);
      }
    },
    async saveManualBlur() {
      const entry = this.currentModalEntry;
      if (!entry || !entry.id || !entry.form_id || !entry['13']) return;
      if (this.isManualBlurring || this.manualBlurRegions.length === 0) return;
      if (!this.isLoggedIn || !auth.sessionToken) {
        this.reblurStatusType = "error";
        this.reblurStatusMessage = "Sign in required to manually blur photos";
        return;
      }

      this.isManualBlurring = true;
      this.reblurStatusMessage = "";
      this.reblurStatusType = "";

      try {
        const { data } = await axios.post(
          '/.netlify/functions/manual-blur-entry',
          {
            entryId: entry.id,
            formId: entry.form_id,
            imageUrl: entry['13'],
            regions: this.manualBlurRegions,
          },
          {
            timeout: 60000,
            headers: { Authorization: `Bearer ${auth.sessionToken}` },
          }
        );

        if (data && data.saved && data.newImageUrl) {
          const bust = `${data.newImageUrl}${data.newImageUrl.includes('?') ? '&' : '?'}t=${Date.now()}`;
          entry['13'] = bust;
          this.selectedImage = bust;
          this.reblurStatusType = "success";
          this.reblurStatusMessage = `Blurred ${data.regionsBlurred} region${data.regionsBlurred === 1 ? '' : 's'} and saved`;
          // Exit manual mode on success.
          this.isManualBlurMode = false;
          this.manualBlurRegions = [];
          this.manualDraft = null;
        } else {
          this.reblurStatusType = "error";
          this.reblurStatusMessage = (data && data.error) || 'Manual blur failed';
        }
      } catch (err) {
        const detail = err.response?.data?.error || err.message || 'Network error';
        this.reblurStatusType = "error";
        this.reblurStatusMessage = `Manual blur failed: ${detail}`;
      } finally {
        this.isManualBlurring = false;
        const lifeMs = this.reblurStatusType === 'success' ? 5000 : 8000;
        const myMsg = this.reblurStatusMessage;
        setTimeout(() => {
          if (this.reblurStatusMessage === myMsg) {
            this.reblurStatusMessage = "";
            this.reblurStatusType = "";
          }
        }, lifeMs);
      }
    },
    goToPreviousCustomer() {
      if (this.currentModalIndex > 0) {
        this.currentModalIndex--;
        this.updateModalContent();
        this.swipeOffset = 0; // Reset swipe offset
      }
    },
    goToNextCustomer() {
      if (this.currentModalIndex < this.availableEntries.length - 1) {
        this.currentModalIndex++;
        this.updateModalContent();
        this.swipeOffset = 0; // Reset swipe offset
      }
    },
    handleTouchStart(e) {
      if (!this.showImageModal) return;
      // In manual-blur mode the overlay owns touch events for drawing — don't
      // let the swipe handler also fire (it would navigate mid-draw).
      if (this.isManualBlurMode) return;
      this.touchStartX = e.touches[0].clientX;
      this.touchStartY = e.touches[0].clientY;
      this.isSwiping = false;
    },
    handleTouchMove(e) {
      if (!this.showImageModal) return;
      if (this.isManualBlurMode) return;

      const touchX = e.touches[0].clientX;
      const touchY = e.touches[0].clientY;
      const deltaX = touchX - this.touchStartX;
      const deltaY = touchY - this.touchStartY;
      
      // Only start swiping if horizontal movement is greater than vertical
      if (Math.abs(deltaX) > Math.abs(deltaY) && Math.abs(deltaX) > 10) {
        e.preventDefault(); // Prevent scrolling while swiping
        this.isSwiping = true;
        // Limit swipe offset to prevent over-swiping
        this.swipeOffset = Math.max(-100, Math.min(100, deltaX));
      }
    },
    handleTouchEnd(e) {
      if (this.isManualBlurMode) return;
      if (!this.showImageModal || !this.isSwiping) {
        this.swipeOffset = 0;
        return;
      }
      
      const touchEndX = e.changedTouches[0].clientX;
      const deltaX = touchEndX - this.touchStartX;
      const swipeThreshold = 50; // Minimum swipe distance to trigger navigation
      
      if (Math.abs(deltaX) > swipeThreshold) {
        if (deltaX > 0) {
          // Swipe right - go to previous
          this.goToPreviousCustomer();
        } else {
          // Swipe left - go to next
          this.goToNextCustomer();
        }
      } else {
        // Reset if swipe wasn't far enough
        this.swipeOffset = 0;
      }
      
      this.isSwiping = false;
    },
    closeImageModal() {
      this.showImageModal = false;
      this.selectedImage = null;
      this.selectedName = "";
      this.currentModalIndex = 0;
      this.swipeOffset = 0;
      this.isSwiping = false;
      this.reblurStatusMessage = "";
      this.reblurStatusType = "";
      this.isManualBlurMode = false;
      this.manualBlurRegions = [];
      this.manualDraft = null;
      this.manualDragStart = null;

      // Remove the class to allow scrolling again
      document.body.classList.remove("overflow-hidden");

      // Remove keyboard event listener
      window.removeEventListener("keydown", this.handleModalKeyEvents);
    },
    handleModalKeyEvents(event) {
      // In manual-blur mode, Escape exits the tool (not the whole modal) and
      // arrow keys are ignored so an accidental keypress can't navigate away
      // from a half-drawn selection.
      if (this.isManualBlurMode) {
        if (event.key === "Escape") {
          event.preventDefault();
          this.cancelManualBlur();
        }
        return;
      }
      // Close modal on Escape key
      if (event.key === "Escape") {
        this.closeImageModal();
      }
      // Navigate with arrow keys
      if (event.key === "ArrowLeft") {
        event.preventDefault();
        this.goToPreviousCustomer();
      }
      if (event.key === "ArrowRight") {
        event.preventDefault();
        this.goToNextCustomer();
      }
    },
    findPhoneField(entry) {
      // Based on the JSON data, the phone number is in field '8'


      // Try field 8 which contains the phone number based on the JSON data
      if (
        entry["8"] &&
        String(entry["8"]).trim() !== "" &&
        String(entry["8"]) !== "[]" &&
        String(entry["8"]) !== "null"
      ) {
        return entry["8"];
      }

      // Try field 11 based on common form structures
      if (
        entry["11"] &&
        String(entry["11"]).trim() !== "" &&
        String(entry["11"]) !== "[]" &&
        String(entry["11"]) !== "null"
      ) {
        return entry["11"];
      }

      // Try field 10 which sometimes contains phone
      if (
        entry["10"] &&
        String(entry["10"]).trim() !== "" &&
        String(entry["10"]) !== "[]" &&
        String(entry["10"]) !== "null"
      ) {
        return entry["10"];
      }

      // Try field 5 which sometimes contains phone
      if (
        entry["5"] &&
        String(entry["5"]).trim() !== "" &&
        String(entry["5"]) !== "[]" &&
        String(entry["5"]) !== "null"
      ) {
        return entry["5"];
      }

      // Finally fallback to field 3
      return entry["3"];
    },

    formatPhone(phone) {
      // First ensure phone is a string
      if (phone === undefined || phone === null) {
        return "No Phone";
      }

      // Convert to string if it's not already
      const phoneStr = String(phone);

      // Check if it's empty or just whitespace
      if (phoneStr.trim() === "" || phoneStr === "[]" || phoneStr === "null") {
        return "No Phone";
      }

      // Handle array notation in string form
      if (phoneStr.includes("[") && phoneStr.includes("]")) {
        try {
          // Try to parse it as a JSON array
          const parsed = JSON.parse(phoneStr.replace(/'/g, '"'));
          if (Array.isArray(parsed) && parsed.length > 0) {
            // Make sure we're returning a string value from the array
            return parsed[0] ? String(parsed[0]).trim() : "No Phone";
          }
          return "No Phone";
        } catch (e) {
          // If parsing fails, just clean up the string
          return phoneStr.replace(/[[\]']/g, "").trim() || "No Phone";
        }
      }

      // Return the cleaned phone number
      return phoneStr.trim();
    },
    nextPage() {
      // Force recalculation of computed properties
      const calculatedTotalPages = Math.max(
        1,
        Math.ceil(this.filteredEntries.length / this.dynamicEntriesPerPage)
      );

      if (this.currentPage < calculatedTotalPages) {
        this.currentPage++;

        // Force update to ensure the view refreshes
        this.$forceUpdate();
      }
    },
    prevPage() {
      if (this.currentPage > 1) {
        this.currentPage--;

        // Force update to ensure the view refreshes
        this.$forceUpdate();
      }
    },
  },
  filters: {
    firstTwoLetters(value) {
      if (!value) return "";
      value = value.toString();
      return value.length >= 2 ? value.substring(0, 2) : value;
    },
  },
};
</script>

<style>
/* Your component-specific styles here */

/* Contact Detail Button Styling - Following Apple Glass Style Guide */
.contact-detail-button {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 6px;
  min-width: 0;
  height: 50px; /* Match height of Load All/Download CSV buttons */
  min-height: 50px; /* Ensure minimum height on mobile */
  border-radius: 9999px; /* Full pill shape */
  transition: all 0.2s ease;
  cursor: pointer;
  border: none;
  font-weight: 500;
  padding: 0 24px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
}

@media (min-width: 640px) {
  .contact-detail-button {
    min-width: 120px;
    padding: 0 24px;
    gap: 8px;
    height: 50px; /* Match height of Load All/Download CSV buttons on desktop */
    border-radius: 32px;
  }
}

.contact-detail-button:hover {
  transform: scale(1.03);
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.25);
}

.contact-detail-button:active {
  transform: scale(0.98);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}

/* Enhanced search input styling to match dashboard */
.search-input {
  color: white !important;
  background: rgba(0, 0, 0, 0.4) !important;
  backdrop-filter: blur(16px) !important;
  border: 1px solid rgba(255, 255, 255, 0.2) !important;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1) !important;
  -webkit-appearance: none !important;
  -moz-appearance: none !important;
  appearance: none !important;
}

.search-input::placeholder {
  color: rgba(156, 163, 175, 0.8) !important;
  opacity: 1 !important;
}

.search-input:focus {
  color: white !important;
  background: rgba(0, 0, 0, 0.6) !important;
  border-color: rgba(34, 197, 94, 0.5) !important;
  outline: none !important;
  box-shadow: 0 0 0 2px rgba(34, 197, 94, 0.4), 0 10px 25px rgba(0, 0, 0, 0.2) !important;
}

/* Override any global input styles */
input[type="text"].search-input {
  color: white !important;
  -webkit-text-fill-color: white !important;
  opacity: 1 !important;
  -webkit-opacity: 1 !important;
}

input[type="text"].search-input::placeholder {
  color: rgba(156, 163, 175, 0.8) !important;
  opacity: 1 !important;
}

/* Mobile specific fixes */
@media (max-width: 768px) {
  .search-input {
    font-size: 16px !important; /* Prevents zoom on iOS */
    background: rgba(0, 0, 0, 0.6) !important;
  }
  
  /* Improve mobile list view */
  .contact-detail-button {
    height: 50px; /* Match height of Load All/Download CSV buttons */
    border-radius: 9999px; /* Full pill shape */
    padding: 0 20px;
    min-height: 50px;
  }
  
  /* Make profile images smaller on mobile */
  .rounded-full.object-cover {
    height: 70px !important;
    width: 70px !important;
  }
}

/* Extra small screens */
@media (max-width: 384px) {
  .contact-detail-button {
    padding: 0 8px;
    min-height: 50px;
    height: 50px;
  }
  
  /* Further reduce profile image size */
  .rounded-full.object-cover {
    height: 60px !important;
    width: 60px !important;
  }
  
  /* Improve toggle appearance on small screens */
  .flex.items-center.justify-center.bg-black\/40 {
    padding: 6px 10px;
  }
}

/* Modal swipe animations */
.modal-image-container {
  transition: transform 0.2s ease-out;
  touch-action: pan-y pinch-zoom;
}

.modal-image-container img.swiping {
  transition: none;
}

/* Prevent text selection during swipe */
.modal-image-container img {
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  user-select: none;
  -webkit-touch-callout: none;
}

/* Smooth navigation button animations */
button[aria-label="Previous customer"],
button[aria-label="Next customer"] {
  opacity: 0.9;
  transition: all 0.2s ease;
}

button[aria-label="Previous customer"]:hover,
button[aria-label="Next customer"]:hover {
  opacity: 1;
  transform: scale(1.1);
}

/* Mobile optimizations for swipe */
@media (max-width: 768px) {
  button[aria-label="Previous customer"],
  button[aria-label="Next customer"] {
    padding: 12px;
  }
  
  button[aria-label="Previous customer"] svg,
  button[aria-label="Next customer"] svg {
    width: 24px;
    height: 24px;
  }
}
</style>
